import React, { FC, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Icon, Message } from 'semantic-ui-react';
import DocumentVersionMini from './DocumentVersionMini';
import DocumentDropZone from './DocumentDropZone';
import { useCentralCalls } from '@src/rest/CentralCalls';


interface DocumentOfferingManagementProps {
    clientId: string;
    clientOfferingId: string;
    sowId:string;
}

interface DocumentTypeTab {
    id: string;
    name: string;
}

const getCaseInsensitiveValue = <T = unknown>( obj: unknown, key: string ): T | undefined => {
    if( !obj || typeof obj !== 'object' ) return undefined;
    const record = obj as Record<string, unknown>;
    const foundKey = Object.keys( record ).find( k => k.toLowerCase() === key.toLowerCase() );
    return foundKey ? ( record[ foundKey ] as T ) : undefined;
};

const parseArrayPayload = ( response: any ): any[] => {
    if( Array.isArray( response ) ) return response;

    const candidates = [ response?.result, response?.Result, response?.data, response?.Data ];
    for( const candidate of candidates ){
        if( Array.isArray( candidate ) ) return candidate;
        if( typeof candidate === 'string' ){
            try {
                const parsed = JSON.parse( candidate );
                if( Array.isArray( parsed ) ) return parsed;
            } catch {
                // ignore bad JSON and keep checking
            }
        }
    }

    if( typeof response === 'string' ){
        try {
            const parsed = JSON.parse( response );
            if( Array.isArray( parsed ) ) return parsed;
        } catch {
            // ignore bad JSON
        }
    }

    return [];
};

const normalizeTabs = ( payload: any[] ): DocumentTypeTab[] =>
    payload
        .map( row => {
            const id = getCaseInsensitiveValue<string>( row, 'Id' );
            const name = getCaseInsensitiveValue<string>( row, 'Name' );

            if( !id || !name ) return null;

            return {
                id,
                name,
            };
        } )
        .filter( ( item ): item is DocumentTypeTab => item != null );

const DocumentOfferingManagement: FC<DocumentOfferingManagementProps> = ( { clientId, clientOfferingId, sowId } ) => {

    const { TableSelect, RunInsert, uploadDocumentToStorage, addSharepointLinkVersion } = useCentralCalls();

    const [ tabs, setTabs ]                     = useState<DocumentTypeTab[]>( [] ),
          [ activeTabIndex, setActiveTabIndex ] = useState(0),
          [ isLoading, setIsLoading ]           = useState( true ),
          [ errorMessage, setErrorMessage ]     = useState( '' ),
          [ refreshMiniKey, setRefreshMiniKey ] = useState(0);

    useEffect( () => {
        if( !clientId || !clientOfferingId ){
            setTabs( [] );
            setActiveTabIndex( 0 );
            setIsLoading( false );
            setErrorMessage( '' );
            return;
        }

        let cancelled = false;

        const load = async () => {
            setIsLoading( true );
            setErrorMessage( '' );

            try {
                const response = await TableSelect( 'TngDocumentTypes', { OfferingLevel: 1 } );

                const payload = parseArrayPayload( response );
                const normalizedTabs = normalizeTabs( payload );

                if( cancelled ) return;

                setTabs( normalizedTabs );
                setActiveTabIndex( 0 );
            } catch( err ){
                if( cancelled ) return;
                const message = err instanceof Error ? err.message : 'Failed to load document offering data.';
                setErrorMessage( message );
                setTabs( [] );
            } finally {
                if( !cancelled ){
                    setIsLoading( false );
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
        // NOTE: TableSelect comes from a hook that returns new function
        // references during rerenders; including it here can retrigger this effect
        // repeatedly and spam the API. clientOfferingId is the real trigger.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ clientId, clientOfferingId ] );

    const activeTab = tabs[ activeTabIndex ];

    const handleUploadNewDocumentItem = async ( file: File, documentName: string ) => {
        if( !activeTab?.id ){
            throw new Error( 'No active document type selected.' );
        }

        const newDocumentItemId = uuid();

        await RunInsert( 'DocumentItems', {
            Id: newDocumentItemId,
            ClientId: clientId,
            ClientOfferingId: clientOfferingId,
            DocumentTypeId: activeTab.id,
            Name: documentName.trim() || file.name,
        } );

        await uploadDocumentToStorage( clientId, newDocumentItemId, file );
        setRefreshMiniKey( prev => prev + 1 );
    };

    const handleAddSharepointDocumentItem = async ( sharepointLink: string, documentName: string ) => {
        if( !activeTab?.id ){
            throw new Error( 'No active document type selected.' );
        }

        const newDocumentItemId = uuid();

        await RunInsert( 'DocumentItems', {
            Id: newDocumentItemId,
            ClientId: clientId,
            ClientOfferingId: clientOfferingId,
            DocumentTypeId: activeTab.id,
            Name: documentName.trim(),
        } );

        await addSharepointLinkVersion( clientId, newDocumentItemId, sharepointLink );
        setRefreshMiniKey( prev => prev + 1 );
    };

    return (
        <div className={ 'document-offering-management' }>
            { errorMessage && (
                <Message
                    negative
                    icon={ 'warning circle' }
                    className={ 'document-offering-management-error' }
                    content={ errorMessage }
                />
            ) }

            { isLoading && (
                <div className={ 'document-offering-management-empty' }>
                    <Icon name={ 'spinner' } loading/> Loading document offerings...
                </div>
            ) }

            { !isLoading && tabs.length === 0 && !errorMessage && (
                <div className={ 'document-offering-management-empty' }>
                    No document offering data found.
                </div>
            ) }

            { !isLoading && tabs.length > 0 && (
                <>
                    <div className={ 'document-offering-management-tabs' }>
                        { tabs.map( ( tab, index ) => (
                            <button
                                key={ `${ tab.id }-${ index }` }
                                type={ 'button' }
                                className={ `document-offering-management-tab${ index === activeTabIndex ? ' active' : '' }` }
                                onClick={ () => setActiveTabIndex( index ) }
                            >
                                { tab.name.trim() || `Tab ${ index + 1 }` }
                            </button>
                        ) ) }
                    </div>

                    { activeTab && (
                        <div className={ 'document-offering-management-panel' }>
                            <DocumentVersionMini
                                key={ `${ activeTab.id }-${ refreshMiniKey }` }
                                clientId={ clientId }
                                clientOfferingId={ clientOfferingId }
                                sowId={ sowId }
                                documentTypeId={ activeTab.id }
                                LabelName={ activeTab.name }
                                showCreateItemRow={ false }
                            />

                            <div className={ 'document-offering-management-upload-divider' } />

                            <DocumentDropZone
                                clientId={ clientId }
                                documentItemId={ '' }
                                requireDocumentName
                                onCustomUpload={ handleUploadNewDocumentItem }
                                onCustomSharepointLink={ handleAddSharepointDocumentItem }
                            />
                        </div>
                    ) }
                </>
            ) }
        </div>
    );
};

export default DocumentOfferingManagement;
